import React, {useEffect, useState} from 'react';
import {ContentAdDesktop} from 'app/styles/content';
import {CaptionContainer, Subheading} from 'app/styles/heading';
import {COLORS, FONTS, Placeholder, Typography} from '@fupa/fupa-uikit';
import styled from 'styled-components';
import {List, ListItem, ListItemSecondaryAction, ListItemText, IconButton, ListItemIcon} from '@mui/material';
import {Delete, Add, Computer, Smartphone, TabletMac, Devices} from '@mui/icons-material';
import {deleteDevice} from 'app/services/notifications';
import {useDispatch, useSelector} from 'react-redux';
import {fetchDevices} from 'app/actions/currentUserActions';
import {selectCurrentUser} from 'app/hooks/reduxCreateSelectorHooks';
import {DMHintBanner} from 'app/components/banner/DMHintBanner';
import {formatLongDate} from 'app/helpers/dateHelpers';
import {changeHeaderText} from 'app/actions/headerActions';
import {Helmet} from 'react-helmet-async';
import {TITLE_POSTFIX} from 'app/seo/seo.constants';
import {captureSentryException} from 'app/services/sentry/sentryLogging';
import {useNative} from 'app/contexts/NativeContext';

const SubheadingCustom = styled(Subheading)`
  &&& {
    margin-bottom: 0;
  }
`;

const CurrentDevice = styled.span`
  color: ${COLORS.green};
  font-weight: ${FONTS.bold};
`;

const getDeviceIcon = {
  mobile: <Smartphone />,
  tablet: <TabletMac />,
  desktop: <Computer />,
};

const DeviceManagementPage = () => {
  const {devices, devicesError, pushSupport, messagingDeviceToken, notificationPermission} =
    useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const nativeInfo = useNative();

  useEffect(() => {
    const parent = {
      name: 'Favoriten',
      entity: 'follows',
    };
    dispatch(changeHeaderText({title: 'Geräte verwalten', parent}));
  }, [dispatch]);

  const checkDeviceState = () => {
    const deviceSubscribed = devices.find(device => device.id === messagingDeviceToken);
    return pushSupport && !deviceSubscribed;
  };

  const [show, setShow] = useState(() => checkDeviceState());
  useEffect(() => {
    setShow(() => checkDeviceState());
  }, [devices]);

  const handleDelete = async id => {
    try {
      await deleteDevice(id);
      dispatch(fetchDevices());
    } catch (error) {
      captureSentryException(error, {extra: {errorLocation: 'delete Device on DeviceManagementPage'}});
    }
  };

  const handleAdd = async () => {
    if (messagingDeviceToken) {
      try {
        const {addDevice} = await import('app/services/firebaseMessaging');
        await addDevice(messagingDeviceToken, dispatch, 'import', nativeInfo.isNativeApp);
      } catch (error) {
        captureSentryException(error, {extra: {errorLocation: 'importing addDevice in DeviceManagementPage'}});
      }
    } else {
      try {
        const {getMessagingDeviceToken} = await import('app/services/firebaseMessaging');
        getMessagingDeviceToken(nativeInfo, dispatch, false, notificationPermission);
      } catch (error) {
        captureSentryException(error, {
          extra: {errorLocation: 'importing getMessagingDeviceToken in DeviceManagementPage'},
        });
      }
    }
  };

  const deviceRows = devices.map(device => {
    const icon = getDeviceIcon[device.deviceType] ?? <Devices />;
    const primaryTextCurrentDevice = `${device.platform}`;
    const secondaryTextCurrentDevice = (
      <>
        {device.browser} – <CurrentDevice>Jetzt aktiv</CurrentDevice>
      </>
    );
    const secondaryText = `${device.browser} – ${formatLongDate(device.updatedAt)} Uhr`;
    const currentDevice = device.id === messagingDeviceToken;

    if (currentDevice) {
      return (
        <ListItem key={device.id}>
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText primary={primaryTextCurrentDevice} secondary={secondaryTextCurrentDevice} />
        </ListItem>
      );
    }

    return (
      <ListItem key={device.id}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={device.platform} secondary={secondaryText} />
        <ListItemSecondaryAction>
          <IconButton edge='end' onClick={() => handleDelete(device.id)} size='large'>
            <Delete color={'action'} />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    );
  });
  const addCurrentDevice = show ? (
    <List>
      <ListItem disabled={notificationPermission === 'denied'}>
        <ListItemText primary='Aktuelles Gerät hinzufügen' />
        <ListItemSecondaryAction>
          <IconButton disabled={notificationPermission === 'denied'} edge='end' onClick={handleAdd} size='large'>
            <Add color={notificationPermission === 'denied' ? 'disabled' : 'secondary'} />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    </List>
  ) : null;

  const content = devicesError ? (
    <Placeholder>
      Beim Laden der Geräte mit aktivierten Push-Benachrichtigungen ist ein Fehler aufgetreten. Versuche es später
      erneut.
    </Placeholder>
  ) : (
    <>
      <SubheadingCustom variant='subheading2'>Geräte</SubheadingCustom>
      <CaptionContainer>
        <Typography variant='caption1'>
          Verwalte hier deine Geräte, für die du Push-Benachrichtigungen erhalten möchtest.
        </Typography>
      </CaptionContainer>
      <DMHintBanner />
      {addCurrentDevice}
      <List>{deviceRows}</List>
    </>
  );

  return (
    <ContentAdDesktop noTabs top='4.5rem'>
      <Helmet title={`Geräte verwalten${TITLE_POSTFIX}`} />
      {content}
    </ContentAdDesktop>
  );
};

export default DeviceManagementPage;
